import { all, call, fork, put, race, select } from 'redux-saga/effects';

import { GeneratorResult, OperationStatus, selectTyped, takeIf } from '@pressreader/appstore';
import { loadIssueRestrictionsByIssueKey } from '@pressreader/content';
import { getArticle, getArticles, type IArticleDto, mapArticle } from '@pressreader/content-api';
import { ArticleAccess, ArticleFields, IArticle } from '@pressreader/content-types';
import { getKnownMissingArticleFields } from '@pressreader/content-utils';
import { ArticleLoadingTags } from '@pressreader/feed-types';
import { error } from '@pressreader/logger';
import { ExtractReturnType } from '@pressreader/types';
import { selectScreenInfo } from '@pressreader/ui-common';
import { hasFlag } from '@pressreader/utils';

import { selectImagesConfig } from '../../global.selectors';
import { mergeArticlesByAvailableFields } from '../../utils/article/merge.articles.by.available.fields';
import { articleUpdated, loadArticleBegan, loadArticleDone, loadArticleFailed } from '../actions';
import { selectArticleByIdAndLanguage, selectArticleMetaById, selectorContentIsHyphenated } from '../selectors';

import { loadArticleBookmarks } from './share';

interface ArticleLoadOptions {
    /** Fields that should be acquired. */
    requiredFields: ArticleFields;

    /** Forcefully load article data. */
    force?: boolean;

    /**
     * Translation language.
     *
     * `string` requests that language, `null` requests the original language explicitly, and
     * `undefined` leaves the decision to the cached version's own language.
     */
    lang?: string | null;

    /** If access requires confirmation, do it. */
    confirm?: boolean;

    /** Define if Hyphenated is enabled, default true */
    isHyphenated?: boolean;

    /**
     * When true, known restricted fields are ignored during missing-fields pruning.
     * Used for access-transition reloads when article access may have changed.
     */
    resetRestrictions?: boolean;
}

function* createArticleFromDTO(articleDto: IArticleDto, requiredFields: ArticleFields): GeneratorResult<IArticle> {
    if (!articleDto) {
        throw new Error('Parse article failed.');
    }

    const screenInfo: ReturnType<typeof selectScreenInfo> = yield select(selectScreenInfo);
    const { imageServers, imagesSupported }: ReturnType<typeof selectImagesConfig> = yield select(selectImagesConfig);
    const article = mapArticle({ screenInfo, imageServers, imagesSupported }, articleDto, requiredFields);

    return article;
}

function* getOrWaitForArticle(articleId: string, lang?: IArticle['language'] | null): GeneratorResult<IArticle | null> {
    const articleMeta: ReturnType<typeof selectArticleMetaById> = yield select(selectArticleMetaById, { articleId });

    if (!articleMeta) {
        return null;
    }

    if (articleMeta.loading !== OperationStatus.None) {
        if (articleMeta.loading === OperationStatus.Done) {
            // `null` is a requested language too — it picks the original-language version.
            if (lang !== undefined && articleMeta?.article?.language !== lang) {
                return yield* selectTyped(selectArticleByIdAndLanguage, { articleId, language: lang });
            }
            return articleMeta.article;
        }

        if (articleMeta.loading === OperationStatus.Failed) {
            return null;
        }

        yield race([
            takeIf(loadArticleDone, a => a.payload.article.id === articleId),
            takeIf(loadArticleFailed, a => a.payload.articleId === articleId),
        ]);

        return yield* getOrWaitForArticle(articleId, lang);
    }

    return null;
}

/**
 * Load article by ID by either returning cached article or fetching it from server.
 *
 * @returns - either cached or newly loaded article, or null if loading failed.
 */
function* loadArticleById(articleId: string, options: ArticleLoadOptions = { requiredFields: ArticleFields.None }): GeneratorResult<IArticle | null> {
    let { requiredFields, lang: requiredLang, isHyphenated } = options;
    const { force, confirm } = options;
    const article = yield* getOrWaitForArticle(articleId, requiredLang);

    let translationRequired = false;

    if (article?.availableTextLength !== article?.textLength) {
        requiredFields |= ArticleFields.Paragraphs;
    }
    if (article) {
        const isTranslated = article.language !== article.originalLanguage;
        if (requiredLang === undefined) {
            // no language was requested, so keep the cached version's own language
            requiredLang = article.language;
        }

        if (article.originalLanguage === requiredLang) {
            requiredLang = undefined;
        }

        translationRequired = requiredLang ? requiredLang !== article.language : isTranslated;
    }

    if (article) {
        const knownMissingFields = getKnownMissingArticleFields(article, {
            resetRestricted: options.resetRestrictions,
        });
        if (knownMissingFields) {
            requiredFields &= ~knownMissingFields;
        }
    }

    if (!force && article) {
        if (article.access && article.access !== ArticleAccess.Granted) {
            // required fields could be not loaded because user doesn't have access to them,
            // they will never be loaded, so just assume that they are not required
            // it will avoid unecessary call to server
            if (hasFlag(requiredFields, ArticleFields.Paragraphs)) {
                requiredFields &= ~ArticleFields.Paragraphs;
            }
        }

        if (((article.availableFields | article.restrictedFields) & requiredFields) === requiredFields) {
            if (!translationRequired) {
                return article;
            }
        }
    }

    const loadingTags: ArticleLoadingTags[] = [];
    if (translationRequired) {
        loadingTags.push(ArticleLoadingTags.Translation);
    }

    const contentIsHyphenated: ReturnType<typeof selectorContentIsHyphenated> = yield select(selectorContentIsHyphenated);
    isHyphenated = isHyphenated === undefined ? contentIsHyphenated : isHyphenated;

    // do not override with possibleArticle if it is already in the store
    yield put(loadArticleBegan({ articleId, loadingTags, requiredFields }));

    if (article) {
        requiredFields |= article.availableFields;
    }

    try {
        const data = {
            articleId,
            fields: requiredFields,
            // The request carries a language only for a translation: the original is the omitted parameter.
            lang: requiredLang ?? undefined,
            confirm,
            isHyphenated,
        };

        const articleDto: ExtractReturnType<typeof getArticle> = yield call(getArticle, data);
        if (!articleDto) {
            throw new Error('Load article failed.');
        }

        const mappedArticle = yield* createArticleFromDTO(articleDto, requiredFields);
        const mergedArticle = article ? mergeArticlesByAvailableFields(article, mappedArticle) : mappedArticle;

        yield put(loadArticleDone({ article: mergedArticle, requiredFields }));
        return mergedArticle;
    } catch (e) {
        error(e);
        yield put(loadArticleFailed({ articleId, requiredFields }));
    }

    return null;
}

export function* loadArticlesById(params: Parameters<typeof getArticles>[0]): GeneratorResult<IArticle[] | null> {
    try {
        if (params.articleIds.length === 0) {
            return [];
        }

        const articleDTOs: ExtractReturnType<typeof getArticles> = yield getArticles(params);
        const articles: IArticle[] = yield all(
            articleDTOs.filter(article => !!article).map(articleDto => createArticleFromDTO(articleDto, params.fields)),
        );

        yield all(articles.map(article => put(loadArticleDone({ article }))));
        return articles;
    } catch (e) {
        error(new Error('Load articles failed.'), e);
    }
    return null;
}

function* loadArticleAdditionalData(article: IArticle) {
    yield* loadArticleBookmarks(article.primaryArticleId);

    if (article.issue) {
        yield* loadIssueRestrictionsByIssueKey(article.issue.issueKey);
    }
}

export type LoadFullArticleOptions = Pick<ArticleLoadOptions, 'force' | 'confirm' | 'lang' | 'resetRestrictions'> & {
    /**
     * Skip the bookmarks/issue-restrictions fork. For render-critical prewarms (deep-link
     * entries) that fork would add network calls inside the LCP window; the post-commit
     * full-article load performs the lookup instead, outside the LCP window.
     */
    skipAdditionalData?: boolean;
};

function* loadFullArticle(articleId: string, options?: LoadFullArticleOptions): GeneratorResult<IArticle | null> {
    const requiredFields =
        ArticleFields.ShortContent |
        ArticleFields.Issue |
        ArticleFields.Publication |
        ArticleFields.Images |
        ArticleFields.Related |
        ArticleFields.Paragraphs |
        ArticleFields.Similars |
        ArticleFields.Access |
        ArticleFields.Comments |
        ArticleFields.Votes |
        ArticleFields.AnalyticProfiles |
        ArticleFields.Classification |
        ArticleFields.Summary;
    const article = yield* loadArticleById(articleId, { ...options, requiredFields });

    if (article) {
        if (!options?.skipAdditionalData) {
            yield fork(loadArticleAdditionalData, article);
        }
        yield put(articleUpdated({ articleId, article }));
    }

    return article;
}

export { loadArticleById, loadFullArticle };
